import axios from 'axios';

// Client-side wiring against the same-origin WordPress backend.
// We hit the WooCommerce Store API (wc/store/v1) rather than the
// classic wc/v3 REST because Store API is the unauthenticated
// public-storefront surface — no consumer_key/secret needed in
// browser JS. wc/v3 would force exposing API keys client-side.
const api = axios.create({
  baseURL: '/wp-json',
  timeout: 10000,
});

export async function getProducts(params = {}) {
  const { data } = await api.get('/wc/store/v1/products', {
    params: { per_page: 8, ...params },
  });
  return Array.isArray(data) ? data : [];
}

export function fromWcProduct(p) {
  const minor = p?.prices?.currency_minor_unit ?? 2;
  const factor = Math.pow(10, minor);
  const priceCents = parseInt(p?.prices?.price ?? '0', 10);
  const regularCents = parseInt(p?.prices?.regular_price ?? '0', 10);
  const onSale = !!p?.on_sale && regularCents > priceCents;
  return {
    id: p?.id,
    name: decodeEntities(p?.name),
    brand: '',
    price: priceCents / factor,
    originalPrice: onSale ? regularCents / factor : undefined,
    rating: parseFloat(p?.average_rating ?? '0') || 0,
    reviewCount: p?.review_count ?? 0,
    reviews: p?.review_count ?? 0,
    image: p?.images?.[0]?.thumbnail || p?.images?.[0]?.src || '',
    imageAlt: decodeEntities(p?.images?.[0]?.alt || p?.name),
    alt: decodeEntities(p?.images?.[0]?.alt || p?.name),
    description: decodeEntities((p?.short_description || '').replace(/<[^>]+>/g, '').trim()),
    stock: p?.is_in_stock ? (p?.low_stock_remaining ?? 99) : 0,
    inStock: !!p?.is_in_stock,
    isNew: false,
    isBestseller: false,
    discount: onSale ? Math.round(((regularCents - priceCents) / regularCents) * 100) : 0,
    freeShipping: false,
    variants: [],
    badge: onSale ? 'Sale' : null,
    categories: Array.isArray(p?.categories)
      ? p.categories.map((c) => c?.id).filter((id) => typeof id === 'number')
      : [],
    // Available sizes — pulled from the product's Size attribute. Stored as
    // uppercase strings ('S','M','L','XL','XXL','3XL', etc.) so case-insensitive
    // matching is straightforward in the catalog filter.
    sizes: (() => {
      const attrs = Array.isArray(p?.attributes) ? p.attributes : [];
      const sizeAttr = attrs.find((a) => /size/i.test(a?.name || ''));
      if (!sizeAttr) return [];
      return (sizeAttr.terms || [])
        .map((t) => (t?.name || t?.slug || '').toString().toUpperCase().trim())
        .filter(Boolean);
    })(),
  };
}


// Fetch WC product categories with live product counts, returned as a
// nested tree (parent + children[]). WC supports hierarchical categories
// (Dresses → Maxi/Midi/Mini, Skirts → Maxi/Midi/Mini, etc.) and the sidebar
// should reflect that hierarchy so the merchant can browse by family.
//
// Filtering: hide 'uncategorized' (default catch-all). Hide any category
// where the category itself AND all its descendants are zero-count
// (empty branch — no point taking sidebar space).
export async function getCategories() {
  const { data } = await api.get('/wc/store/v1/products/categories', {
    params: { per_page: 100, orderby: 'name', order: 'asc' },
  });
  if (!Array.isArray(data)) return [];

  // Index by id, attach a children[] array
  const byId = new Map();
  for (const c of data) {
    // Skip 'uncategorized' default + Lalay curated collections (surfaced
    // separately in the FilterSidebar's "Collections" section).
    if (c.slug === 'uncategorized') continue;
    if ([60, 61, 62].includes(c.id)) continue;
    byId.set(c.id, {
      id: c.id,
      slug: c.slug,
      label: c.name,
      count: c.count,
      parentId: c.parent || 0,
      children: [],
    });
  }

  // Build tree
  const roots = [];
  for (const node of byId.values()) {
    if (node.parentId && byId.has(node.parentId)) {
      byId.get(node.parentId).children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children alphabetically within each parent
  for (const node of byId.values()) {
    node.children.sort((a, b) => a.label.localeCompare(b.label));
  }
  roots.sort((a, b) => a.label.localeCompare(b.label));

  // Drop empty branches (count=0 AND no children with count>0)
  function hasContent(node) {
    if (node.count > 0) return true;
    return node.children.some(hasContent);
  }
  function pruneEmpty(nodes) {
    return nodes
      .filter(hasContent)
      .map((n) => ({ ...n, children: pruneEmpty(n.children) }));
  }
  return pruneEmpty(roots);
}


// Fetch a single product by ID via WC Store API.
export async function getProduct(id) {
  if (!id) return null;
  const { data } = await api.get(`/wc/store/v1/products/${encodeURIComponent(id)}`);
  return data || null;
}

// Richer mapper for the product-details page — covers description, gallery,
// stock, variants. Overrides every default the child components might fall
// back to with explicit empty strings so beauty placeholders don't leak through.
// CJ-style vendor descriptions ship sections that are useful for the sourcing
// company but confusing for shoppers — the embedded size-chart image (often
// in Chinese), "Note:" caveats about Asian sizing, and the "Packing list:"
// breakdown. Strip those before the customer sees the description. The raw
// content stays intact in wp-admin so the operator can still see everything.
//
// Pattern: each section is introduced by a <b>Section:</b> heading and runs
// until the next <b> tag (or end of string). We match by heading keyword and
// drop the entire block.
// Vendor SKUs from CJ Dropshipping start with "CJ" (e.g. CJQZ1711982).
// Replace that prefix with the brand name in customer-facing surfaces so
// shoppers don't see who we source from. Original SKU stays intact in WC.
// Brand prefix is per-storefront; pull from VITE_BRAND_SKU_PREFIX if set,
// fall back to a sensible default for the lalay deployment.
const BRAND_SKU_PREFIX = (typeof import.meta !== 'undefined'
  && import.meta.env?.VITE_BRAND_SKU_PREFIX)
  || 'LALAY';

export function maskVendorSku(sku) {
  if (!sku) return '';
  return String(sku).replace(/^CJ/i, BRAND_SKU_PREFIX + '-');
}

// Decode HTML entities surfacing as literal text (&#8217;, &amp;, &nbsp;,
// &#x2019; etc.) so they don't show as "Women&#8217;s" in product names.
function decodeEntities(s) {
  if (s == null) return s;
  return String(s)
    .replace(/&#(\d+);/g, (_, n) => { try { return String.fromCodePoint(parseInt(n, 10)); } catch { return _; } })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => { try { return String.fromCodePoint(parseInt(h, 16)); } catch { return _; } })
    .replace(/&nbsp;/gi, ' ')
    .replace(/&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&');
}

// Find a "Specification:" / "Specifications:" block in the description and
// pull it out as structured key:value rows so it can render in the
// Specifications tab instead of leaking into the product description.
// Returns { html, specs } — html has the block removed, specs is a plain object.
function extractSpecificationsBlock(html) {
  if (!html) return { html: '', specs: {} };
  const HEADING = /(?:<b>\s*Specifications?\s*[:：]\s*<\/b>|(?:<br\s*\/?>|<\/p>\s*<p>|^)\s*Specifications?\s*[:：])/i;
  const m = html.match(HEADING);
  if (!m) return { html, specs: {} };
  const startIdx = m.index;
  const afterHeading = startIdx + m[0].length;
  const NEXT_BREAK = /<b>\s*(?:Notes?|Tips?|Reminder|Packing\s*[Ll]ist|Package\s*[Ll]ist|Product\s*Image|Size|Sizing|MOQ|Lead\s*[Tt]ime|Warehouse|Vendor|Supplier)/i;
  const tail = html.slice(afterHeading);
  const breakMatch = tail.match(NEXT_BREAK);
  const endIdx = breakMatch ? afterHeading + breakMatch.index : html.length;
  const block = html.slice(afterHeading, endIdx);
  const cleanedHtml = (html.slice(0, startIdx) + html.slice(endIdx)).trim();
  // Parse the block into key:value rows. Lines are separated by <br> or </p><p>.
  // Each line: "Key: Value". Skip empty values (e.g. "Color:" preceding A/B/C).
  const lines = block
    .replace(/<\/p>\s*<p>/gi, '<br>')
    .replace(/<\/?(?:p|div)>/gi, '<br>')
    .split(/<br\s*\/?>/i)
    .map((s) => s.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim())
    .filter(Boolean);
  const specs = {};
  for (const line of lines) {
    const colonM = line.match(/^([^:：]{1,60})[:：]\s*(.+)$/);
    if (!colonM) continue;
    const key = decodeEntities(colonM[1].trim());
    const val = decodeEntities(colonM[2].trim());
    if (key && val) specs[key] = val;
  }
  return { html: cleanedHtml, specs };
}

function sanitizeCustomerDescription(html) {
  if (!html) return '';
  // Strategy: find the FIRST vendor heading (Size / Note / Packing list / ...).
  // Everything before it is real product content (Product information + specs)
  // and stays. Everything after it is vendor noise — except <img> tags, which
  // are usually the Product Image gallery and must be preserved.
  const CUT_PATTERNS = [
    // <b>{VENDOR HEADING}{colon}</b>  — broad keyword set, only matched inside <b>.
    // Accepts both ASCII colon and fullwidth Chinese colon (\uFF1A) since CJ
    // descriptions sometimes ship the wide-form. Optional trailing colon
    // catches headings written without one.
    /<b>\s*(?:Size\s*Information|Size\s*Chart|Sizing|Size|Notes?|Tips?|Reminder|Packing\s*[Ll]ist|Package\s*[Ll]ist|MOQ|Lead\s*[Tt]ime|Warehouse|Vendor|Supplier)\s*[:\uFF1A]?\s*<\/b>/i,
    // Bare-text variants — STRICT vocab only (Size/Sizing excluded since they
    // appear as legitimate spec attribute names). Requires a colon (ASCII or
    // fullwidth) so we don't false-match the word elsewhere.
    /(?:<br\s*\/?>\s*){1,}\s*(?:Notes?|Tips?|Reminder|Packing\s*[Ll]ist|Package\s*[Ll]ist|MOQ|Lead\s*[Tt]ime|Warehouse|Vendor|Supplier)\s*[:\uFF1A]/i,
  ];
  // Find the earliest cut point across all patterns.
  let cutAt = -1;
  for (const pat of CUT_PATTERNS) {
    const m = html.match(pat);
    if (m && (cutAt < 0 || m.index < cutAt)) cutAt = m.index;
  }
  if (cutAt < 0) return html.trim();
  const before = html.slice(0, cutAt);
  const after  = html.slice(cutAt);
  // Pull every <img> out of the vendor tail. These are the Product Image gallery
  // (CJ puts them under a "Product Image:" heading after the vendor sections),
  // plus occasionally a size-chart image — we keep all of them since shoppers
  // benefit from seeing the product imagery regardless of which CJ section
  // they were originally placed under.
  const images = after.match(/<img[^>]*>/gi) || [];
  let cleaned = before + (images.length ? '\n' + images.join('\n') : '');
  // Scrub placeholder spec lines: "Color: element 1, element 2, ..." or
  // "Size: variant1, variant2, ..." — CJ imports occasionally dump the
  // vendor template's literal placeholder values into a Product information
  // spec line. Detect runs of 2+ such tokens and strip the whole line
  // (anchored to either a leading <br>, paragraph boundary, or start of line).
  cleaned = cleaned.replace(
    /(?:<br\s*\/?>|<\/p>\s*<p>|^)\s*[\w][\w\s/]{0,40}?\s*[:\uFF1A]\s*(?:(?:element|variant|option|choice|item|product|sample|test)\s*\d+[,.\s|]*){2,}\.?\s*/gi,
    ''
  );

  // Trailing-text cleanup: after the last <img> in the cleaned output, strip
  // any text-only <p> blocks. CJ descriptions sometimes carry vendor captions
  // like 'for reference only' or stray notes outside any heading — they're
  // confusing to shoppers and offer no info beyond what the image already shows.
  // Only fires if the description has at least one image AND there's text-only
  // <p>(s) after the last image. Preserves <p> blocks that contain other images.
  const lastImgMatch = cleaned.match(/<img[^>]*>(?![\s\S]*<img)/i);
  if (lastImgMatch) {
    const lastImgEnd = lastImgMatch.index + lastImgMatch[0].length;
    const tail = cleaned.slice(lastImgEnd);
    // If the tail has no images and only text/markup, drop everything after
    // the last <img>'s closing tag (and let any unclosed <p>s self-resolve).
    if (!/<img/i.test(tail)) {
      cleaned = cleaned.slice(0, lastImgEnd);
    }
  }
  // Collapse stray runs of <br> left behind near the cut.
  return cleaned
    .replace(/(\s*<br\s*\/?>\s*){2,}/gi, '<br>')
    .replace(/^\s*(<br\s*\/?>\s*)+/i, '')
    .replace(/(<br\s*\/?>\s*)+\s*$/i, '')
    .trim();
}

export function fromWcProductDetail(p) {
  if (!p) return null;
  const minor = p?.prices?.currency_minor_unit ?? 2;
  const factor = Math.pow(10, minor);
  const priceCents = parseInt(p?.prices?.price ?? '0', 10);
  const regularCents = parseInt(p?.prices?.regular_price ?? '0', 10);
  const onSale = !!p?.on_sale && regularCents > priceCents;
  const images = Array.isArray(p?.images) && p.images.length
    ? p.images.map((img, i) => ({
        id: img?.id ?? i,
        url: img?.src ?? '',
        alt: img?.alt || p?.name || '',
      }))
    : [];
  const { html: descWithoutSpecs, specs: extractedSpecs } = extractSpecificationsBlock(p?.description || '');
  return {
    id: p?.id,
    name: decodeEntities(p?.name || ''),
    brand: '',
    slug: p?.slug || '',
    permalink: p?.permalink || '',
    price: priceCents / factor,
    originalPrice: onSale ? regularCents / factor : undefined,
    rating: parseFloat(p?.average_rating ?? '0') || 0,
    reviewCount: p?.review_count ?? 0,
    inStock: !!p?.is_in_stock,
    stockCount: p?.low_stock_remaining ?? (p?.is_in_stock ? 99 : 0),
    description: decodeEntities(sanitizeCustomerDescription(descWithoutSpecs)),  // Specification: block pulled into specifications; vendor-only sections stripped
    specifications: extractedSpecs,
    shortDescription: decodeEntities((p?.short_description || '').replace(/<[^>]+>/g, '').trim()),
    sku: maskVendorSku(p?.sku || ''),
    weight: p?.formatted_weight || (p?.weight ? String(p.weight) : ''),
    dimensions: p?.formatted_dimensions || '',
    sizeChart: (function () {
      // Merchants can ship a per-product size chart via a custom WC field
      // "size_chart" — pulled through the Store API's metadata bucket if
      // exposed, otherwise undefined and we fall back to the default grid.
      const md = Array.isArray(p?.meta_data) ? p.meta_data : (p?.extensions?.meta_data || []);
      const hit = md.find?.(m => m?.key === 'size_chart' || m?.key === '_size_chart');
      if (!hit) return null;
      try {
        const val = typeof hit.value === 'string' ? JSON.parse(hit.value) : hit.value;
        return (val && typeof val === 'object') ? val : null;
      } catch { return null; }
    })(),
    // Override beauty-only defaults with empty strings so the UI does not
    // fall back to the placeholder "Aqua, Hyaluronic Acid..." text.
    ingredients: '',
    usage: '',
    variants: [],
    // Variation-relevant attributes (Color, Size, etc.) with their available
    // terms. UI uses this to render per-attribute pickers. Empty for simple
    // (non-variable) products.
    attributes: (Array.isArray(p?.attributes) ? p.attributes : [])
      .filter((a) => a?.has_variations)
      .map((a) => ({
        name: decodeEntities(a?.name || ''),
        slug: (a?.name || '').toLowerCase(),
        terms: (Array.isArray(a?.terms) ? a.terms : [])
          .map((t) => decodeEntities((t?.name || t?.slug || '').toString()))
          .filter(Boolean),
      }))
      // Drop attributes whose terms are all placeholder values (Element1,
      // Variant2, Option3 etc.) — CJ imports occasionally ship these as the
      // raw vendor template instead of real attribute values. Showing them
      // to shoppers as selectable swatches would be meaningless.
      .filter((a) => {
        const placeholderRe = /^(element|variant|option|choice|item|product|sample|test)\s*\d+$/i;
        if (!a.terms.length) return false;
        const allPlaceholder = a.terms.every((t) => placeholderRe.test(t.trim()));
        return !allPlaceholder;
      }),
    // Per-variation snapshot — id + attribute combination only. Price/stock
    // come from the parent product by default; if needed we can fetch
    // individual variation detail on selection later.
    variations: (Array.isArray(p?.variations) ? p.variations : []).map((v) => ({
      id: v?.id,
      // attributes shipped as [{name, value}] — flatten to {name -> value}
      attributes: Object.fromEntries(
        (Array.isArray(v?.attributes) ? v.attributes : [])
          .map((a) => [(a?.name || '').toLowerCase(), (a?.value || '').toString()])
      ),
    })),
    features: [],
    images,
    categories: Array.isArray(p?.categories)
      ? p.categories.map((c) => ({ id: c?.id, name: decodeEntities(c?.name), slug: c?.slug }))
      : [],
  };
}

// Fetch product reviews from the WC Store API. Reviews are WP comments on
// the product post, so they roundtrip through wp-admin → Products → Reviews
// where the merchant can moderate, reply, and approve.
export async function getProductReviews(productId, opts = {}) {
  if (!productId) return [];
  const params = {
    product_id: productId,
    per_page: opts.per_page ?? 20,
    orderby: opts.orderby ?? 'rating',
    order: opts.order ?? 'desc',
  };
  try {
    const { data } = await api.get('/wc/store/v1/products/reviews', { params });
    return Array.isArray(data) ? data : [];
  } catch (e) {
    return [];
  }
}


// Fetch per-variation details (image, price, stock, attributes) for a
// variable product. Used by the product detail page to render image-swatch
// pickers when an attribute has gibberish placeholder terms like Element1.
export async function getProductVariations(productId) {
  if (!productId) return [];
  try {
    const res = await fetch(`/wp-json/lalay/v1/products/${encodeURIComponent(productId)}/variations`, {
      headers: { 'Accept': 'application/json' },
    });
    const body = await res.json().catch(() => null);
    if (res.ok && body?.success && Array.isArray(body.variations)) return body.variations;
  } catch {}
  return [];
}
